import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { MerchantLogo, P, Text } from '@mxenabled/mxui'
import { ChevronRight } from '@mxenabled/mx-icons'

import { ConnectDrawer, Drawer, useCategoryStore, useGlobalCopyStore } from '../../../common'
import { Transaction } from '../../../core'
import { fromUnixToDayOfWeekAndDate } from '../../../core/utils/DateUtil'
import { TransactionList } from '../../../transactions'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

interface TransactionsProps {
  transactions: Transaction[]
  amountColor?: string
  amountPosition?: 'flex-start' | 'center' | 'flex-end'
  formatDate?: (date: number) => string
  secondaryIcon?: React.ReactNode
}

const Transactions: React.FC<TransactionsProps> = ({
  transactions = [],
  amountColor = 'success.main',
  amountPosition = 'center',
  formatDate = fromUnixToDayOfWeekAndDate,
  secondaryIcon,
}) => {
  const theme = useTheme()
  const { connect: connectCopy, finstrong: copy, categories: categoryCopy } = useGlobalCopyStore()
  const [isOpen, setIsOpen] = React.useState(false)
  const [isConnectAccountsOpen, setIsConnectAccountsOpen] = React.useState(false)
  const { getCategoryName } = useCategoryStore()

  const maxVisible = 3
  const hasMoreTransactions = transactions.length > maxVisible
  const remainingCount = transactions.length - maxVisible

  let updatedTransactions = transactions
  if (hasMoreTransactions) {
    updatedTransactions = transactions
      .map((eachTransaction) => {
        return {
          ...eachTransaction,
          category:
            categoryCopy.default_categories[eachTransaction.top_level_category_guid!]?.replace(
              /&amp;/g,
              '&',
            ) ?? getCategoryName(eachTransaction.top_level_category_guid!),
        }
      })
      .sort((a, b) => b.date - a.date)
  }
  if (!transactions || transactions.length === 0) {
    return (
      <>
        <Box>
          <Button
            onClick={() => setIsConnectAccountsOpen(true)}
            size="small"
            sx={{ my: 0, px: 16, py: 12, width: '100%' }}
            variant="contained"
          >
            <Text bold={true} variant="body1">
              {copy.connect_more_accounts_button}
            </Text>
          </Button>
        </Box>
        <ConnectDrawer
          onClose={() => setIsConnectAccountsOpen(false)}
          showConnectWidget={isConnectAccountsOpen}
          title={connectCopy.mini_title}
        />
      </>
    )
  }

  return (
    <Stack sx={{ gap: 16 }}>
      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {transactions
          .slice(0, maxVisible)
          .sort((a, b) => b.date - a.date)
          .map((item, index) => (
            <Stack
              key={index}
              sx={{
                alignItems: 'center',
                flexDirection: 'row',
                gap: 16,
                pl: 24,
                pt: 14,
              }}
            >
              <Stack sx={{ pb: 14 }}>
                <MerchantLogo
                  categoryGuid={item.top_level_category_guid || ''}
                  merchantGuid={item.merchant_guid || ''}
                  size={32}
                />
              </Stack>
              <Stack
                key={item.guid || index}
                sx={{
                  alignItems: amountPosition,
                  flexDirection: 'row',
                  gap: 12,
                  justifyContent: 'space-between',
                  ...(index < Math.min(transactions.length, maxVisible) - 1 && {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }),
                  pb: 13,
                  pr: 24,
                  width: '100%',
                }}
              >
                <Stack>
                  <P bold={true} variant="body1">
                    {item.description}
                  </P>
                  <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    {!!secondaryIcon && secondaryIcon}
                    <Text variant="caption">{formatDate(item.date)}</Text>
                  </Stack>
                </Stack>

                <P bold={true} sx={{ color: amountColor, flexShrink: 0 }} variant="body1">
                  {formatCurrency(item.amount, '0,0.00')}
                </P>
              </Stack>
            </Stack>
          ))}
      </Box>

      {hasMoreTransactions && (
        <>
          <Box>
            <Button
              endIcon={<ChevronRight />}
              onClick={() => setIsOpen(true)}
              size="small"
              sx={{ my: 0, px: 16, py: 12, width: '100%' }}
            >
              <Text bold={true} variant="body1">
                {remainingCount}&nbsp;
                {
                  copy.key_indicators_borrow.have_manageable_debt_drawer
                    .have_manageable_debt_more_transactions
                }
              </Text>
            </Button>
          </Box>
          <Drawer
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title={
              copy.key_indicators_borrow.have_manageable_debt_drawer
                .have_manageable_debt_transactions
            }
          >
            <TransactionList transaction={updatedTransactions} />
          </Drawer>
        </>
      )}
    </Stack>
  )
}

export default Transactions
